import axios, {Method} from "axios";
import {useAuthStore} from "../store/authStore";
import {auth} from "../firebaseConfig";
import {showMessage} from "../utils/snackbar";
import {shouldReload} from "../utils/version";

export const SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY = "show_version_update_notification";

let reloadTriggered = false;

// Create an Axios instance with authentication support
const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL ?? "/api",
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
    },
});

// Automatically add `idToken` to request headers
api.interceptors.request.use(
    (config) => {
        const idToken = useAuthStore.getState().idToken;
        if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => {
        if (reloadTriggered) return response;

        const serverVersion = response.headers["x-api-version"] ?? response.headers["X-Api-Version"];
        const clientVersion = process.env.REACT_APP_VERSION;

        if (shouldReload(serverVersion, clientVersion)) {
            console.info("Reloading page due to version update.");
            console.info("Server version:", serverVersion);
            console.info("Client version:", clientVersion);
            reloadTriggered = true;
            sessionStorage.setItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY, "true");
            window.location.reload();
        }
        return response;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            showMessage("Unauthorized request. Trying to refresh token...", "warning")

            if (auth.currentUser) {
                await auth.currentUser.getIdToken(true)
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Logs request details.
 */
const logRequest = (method: string, url: string, payload?: any) => {
    console.info(`${method}[request] ${url}`, payload ? {payload} : "No payload");
};

/**
 * Logs error details.
 */
const logError = (method: string, url: string, error: any) => {
    console.error(`${method}[response] ${url} - FAILED [${error?.request?.status}] ${error?.code}`, error);
};

/**
 * Measures request execution time.
 */
const measureExecutionTime = async <T>(
    method: string,
    url: string,
    request: () => Promise<T>
): Promise<T> => {
    const start = performance.now();
    try {
        const response = await request();
        const duration = (performance.now() - start).toFixed(2);
        console.info(`${method}[response] ${url} - SUCCESS [${duration}ms]`, response);
        return response;
    } catch (error: any) {
        logError(method, url, error);
        throw error;
    }
};

/**
 * Sends an API event message from an embedded iframe to the parent window.
 *
 * This method is used when an iframe needs to notify the parent frame about
 * API interactions, such as fetching data, submitting forms, or completing
 * authentication flows.
 */
export const publishApiEventToParentFrame = (resource_name: string, payload: any) => {
    if (window.parent === undefined) {
        return;
    }

    // nosemgrep javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration - we restrict embedding through the Content-Security-Policy header
    window.parent.postMessage({resource_name, payload}, "*");
};

// API Methods
function handleRequest<T = any>(method: Method, url: string, payload: any) {
    logRequest(method, url, payload);

    // Detect FormData and adjust headers
    const isFormData = payload instanceof FormData;
    const config: any = {};
    if (isFormData) {
        config.headers = {
            // Let Axios set the correct Content-Type for FormData
            "Content-Type": undefined,
        };
    }

    return measureExecutionTime(method, url, async () => {
        const response = await api.request<T>({
            method: method,
            url,
            data: payload,
            ...config,
        });
        return response.data;
    });
}

export const get = async <T = any>(url: string): Promise<T> => {
    return handleRequest("GET", url, null);
};

export const post = async <T = any>(url: string, payload: any = null): Promise<T> => {
    return handleRequest("POST", url, payload);
};

export const put = async <T = any>(url: string, payload: any): Promise<T> => {
    return handleRequest("PUT", url, payload);
};

export const patch = async <T = any>(url: string, payload: any): Promise<T> => {
    return handleRequest("PATCH", url, payload);
};

export const del = async <T = any>(url: string): Promise<T> => {
    return handleRequest("DELETE", url, null);
};
